// @noflow
import { connect } from 'react-redux';
import { flowRight, fromPairs, last, map, replace, toPairs } from 'lodash/fp';
import { getTimezone } from '~/core/state/hub/selectors';
import infoMessages from './info/intl.js';
import Logs from './component';
import moment from 'moment-timezone';
import { addHours, addMinutes, utc } from 'instadate';
import { messages as timeMessages } from '~/core/constants/time-units';

const removePercent = replace('%', '');
const wrapValuesWithPercent = flowRight(fromPairs, map(([x, y]) => [x, `${y}%`]), toPairs);
const unwrapPercentValues = flowRight(fromPairs, map(([x, y]) => [x, removePercent(y)]), toPairs);
const fixPercentStringToDecimal = flowRight(Number, n => n.toFixed(2), Number, removePercent);

/**
 * Returns UTC time of passed in UTC date plus one hour
 * @param  {int} utcDate [utc time of a given date]
 * @return {int} utcDate [utc time of date plus one hour with seconds and millisconds set at 0]
 */
function getHourRoundedUTC(utcDate: number) {
    const originalDate = new Date(utcDate);
    const roundedDate = addHours(originalDate, 1);
    const timezoneOffsetMinutes = roundedDate.getTimezoneOffset();
    roundedDate.setMinutes(0, 0, 0);
    return utc(addMinutes(roundedDate, timezoneOffsetMinutes));
}

type TimePeriod = {
    start: number,
    end: number
};

/**
 * Returns Milliseconds from end - start
 * @param  {int} start UTCDate in Milliseconds
 * @param  {int} end UTCDate in Milliseconds
 * @return {int} end - start
 */
const getMSDuration = ({ start, end }: TimePeriod) => (start && end ? end - start : null);

const MILLISECONDS_IN_HOUR = 3600000;

export const getTimelineEndRounded = timelineEnd => getHourRoundedUTC(timelineEnd);

export const getTickAmount = ({ timelineEnd, timelineStart, zoomLevel }) =>
    1 + (getTimelineEndRounded(timelineEnd) - timelineStart) / MILLISECONDS_IN_HOUR * zoomLevel;

export const getTickSpaceWidth = minTicksDisplayed => 100 / minTicksDisplayed;

export const getPositionOffset = flowRight(tickSpaceWidth => tickSpaceWidth * 0.5, getTickSpaceWidth);

const getMinTicksPositionOffset = ({ minTicksDisplayed, timelineEnd, timelineStart, zoomLevel }) => {
    const tickSpaceWidth = getTickSpaceWidth(minTicksDisplayed);
    const timelineEndRounded = moment(timelineEnd)
        .add(1, 'hour')
        .startOf('hour')
        .valueOf();
    const tickAmount = 1 + (timelineEndRounded - timelineStart) / MILLISECONDS_IN_HOUR * zoomLevel;
    return getPositionOffset(minTicksDisplayed) + Math.max(minTicksDisplayed - tickAmount, 0) * tickSpaceWidth;
};

export const getWidthUnits = flowRight(tickSpaceWidth => tickSpaceWidth / MILLISECONDS_IN_HOUR, getTickSpaceWidth);

export const getTotalWidth = ({ minTicksDisplayed, timelineEnd, timelineStart, zoomLevel }) => {
    const tickAmount = getTickAmount({ timelineEnd, timelineStart, zoomLevel });
    const tickSpaceWidth = getTickSpaceWidth(minTicksDisplayed);

    return tickAmount * tickSpaceWidth;
};

export const getDateFromStart = ({ hubTimezone, start }) =>
    start && hubTimezone ? moment.tz(start, hubTimezone) : null;

export const getRightDistance = ({ positionOffset, timeAsDuration, widthUnits, zoomLevel }) =>
    timeAsDuration * widthUnits * zoomLevel + positionOffset;

export const getEventLogStyle = ({ logTime, minTicksDisplayed, timelineEnd, timelineStart, zoomLevel }) => {
    const timeAsDuration = getMSDuration({
        start: logTime,
        end: getTimelineEndRounded(timelineEnd)
    });
    const positionOffset = getMinTicksPositionOffset({
        minTicksDisplayed,
        timelineEnd,
        timelineStart,
        zoomLevel
    });
    const widthUnits = getWidthUnits(minTicksDisplayed);
    const logRight = getRightDistance({
        positionOffset,
        timeAsDuration,
        widthUnits,
        zoomLevel
    });

    return { right: logRight };
};

export const formatZIndex = index => ({ zIndex: index });

export const mergeStylesWithZIndex = (style, index) => ({
    ...style,
    ...formatZIndex(index)
});

export const getLogBGStyle = ({
    end,
    endStyleValues,
    inMotion,
    minTicksDisplayed,
    start,
    startStyleValues,
    timelineEnd,
    zoomLevel
}) => {
    const widthUnits = getWidthUnits(minTicksDisplayed);
    let logStyleBGWidth;
    let logStyleBGRight;

    if (start && end) {
        logStyleBGRight = endStyleValues.right;
        logStyleBGWidth = startStyleValues.right - endStyleValues.right;
    } else if (start && !end) {
        // Current Event Log in motion
        if (inMotion) {
            const nowWidth = (timelineEnd - start) * widthUnits * zoomLevel;
            logStyleBGRight = startStyleValues.right - nowWidth;
            logStyleBGWidth = nowWidth;
        } else {
            logStyleBGRight = startStyleValues.right;
            logStyleBGWidth = 0;
        }
    } else if (!start && end) {
        //Log from day before
        logStyleBGRight = endStyleValues.right;
        logStyleBGWidth = getPositionOffset(minTicksDisplayed);
    }

    return wrapValuesWithPercent({
        right: logStyleBGRight,
        width: logStyleBGWidth
    });
};

export const getDurationMessage = (formatMessage, duration) => {
    return duration
        ? moment
              .duration(duration)
              .humanize()
              .toString()
              .split(' ')
              .reduce((msg, str) => {
                  const id = timeMessages[str];
                  const returnStr = id ? formatMessage(timeMessages[str]) : str;
                  return [...msg, returnStr];
              }, [])
              .join(' ')
        : null;
};

export const getMultipleEventsMessage = (formatMessage, eventCount) =>
    `${eventCount} ${formatMessage(infoMessages.events)}`;

export const formatLog = ({ formatMessage, hubTimezone, minTicksDisplayed, timelineEnd, timelineStart, zoomLevel }) => (
    { end, start, inMotion, type },
    index,
    { length }
) => {
    const zIndex = length - index;
    const startStyleValues = getEventLogStyle({
        logTime: start,
        minTicksDisplayed,
        timelineEnd,
        timelineStart,
        zoomLevel
    });
    const endStyleValues = getEventLogStyle({
        logTime: end || timelineEnd,
        minTicksDisplayed,
        timelineEnd,
        timelineStart,
        zoomLevel
    });

    const logBGStyle = mergeStylesWithZIndex(
        getLogBGStyle({
            end,
            endStyleValues,
            inMotion,
            minTicksDisplayed,
            start,
            startStyleValues,
            timelineEnd,
            zoomLevel
        }),
        zIndex
    );
    const startStyle = mergeStylesWithZIndex(wrapValuesWithPercent(startStyleValues), zIndex);
    const endStyle = mergeStylesWithZIndex(wrapValuesWithPercent(endStyleValues), zIndex);
    const infoStyle = { right: endStyle.right, zIndex };
    const durationMessage = getDurationMessage(formatMessage, getMSDuration({ start, end }));
    const timeToDisplay = getDateFromStart({ hubTimezone, start });

    return {
        end,
        endStyle,
        durationMessage,
        infoStyle,
        inMotion,
        label: type,
        logBGStyle,
        start,
        startStyle,
        timeToDisplay
    };
};

export const mergeLog = ({ lastLog, currentLog, formatMessage, minTicksDisplayed, timelineEnd }) => {
    const { end: lastEnd, endStyle: lastEndStyle, mergeCount, inMotion, label } = lastLog;
    const { start, startStyle, infoStyle, timeToDisplay } = currentLog;

    const nextMergeCount = mergeCount + 1 || 2;
    const durationMessage = getMultipleEventsMessage(formatMessage, nextMergeCount);

    const logBGStyle = mergeStylesWithZIndex(
        getLogBGStyle({
            end: lastEnd,
            endStyleValues: unwrapPercentValues(lastEndStyle),
            inMotion,
            minTicksDisplayed,
            start,
            startStyleValues: unwrapPercentValues(startStyle),
            timelineEnd
        }),
        startStyle.zIndex
    );

    return {
        end: lastEnd,
        endStyle: lastEndStyle,
        durationMessage,
        infoStyle,
        inMotion,
        label,
        logBGStyle,
        mergeCount: nextMergeCount,
        start,
        startStyle,
        timeToDisplay
    };
};

export const mergeOverlaps = ({ formatMessage, minTicksDisplayed, timelineEnd, mergeThreshold }) => (all, log, i) => {
    let mergedLog = log;

    if (i > 0) {
        const { startStyle: lastStartStyle, inMotion: lastInMotion } = last(all);
        const { endStyle } = log;

        if (lastInMotion) {
            return [...all, log];
        }

        const lastStart = fixPercentStringToDecimal(lastStartStyle.right);
        const currentEnd = fixPercentStringToDecimal(endStyle.right);
        if (currentEnd - lastStart < mergeThreshold) {
            mergedLog = mergeLog({
                currentLog: log,
                lastLog: last(all),
                formatMessage,
                minTicksDisplayed,
                timelineEnd
            });
            //Remove the last log as it will be replaced with the new mergedLog
            all.pop(i - 1);
        }
    }

    return [...all, mergedLog];
};

/**********************
 *****  Component *****
 **********************/
export const getLogsData = ({
    formatMessage,
    hubTimezone,
    mergeThreshold,
    minTicksDisplayed,
    sensorLogs,
    timelineEnd,
    timelineStart,
    zoomLevel
}) => {
    const totalWidth = getTotalWidth({
        minTicksDisplayed,
        timelineEnd,
        timelineStart,
        zoomLevel
    });
    const timelineStyle = { width: `${Math.max(totalWidth, 100)}%` };

    const logs = sensorLogs.map(
        formatLog({
            formatMessage,
            hubTimezone,
            minTicksDisplayed,
            timelineEnd,
            timelineStart,
            zoomLevel
        })
    );
    const mergedLogs = logs.reduce(
        mergeOverlaps({
            formatMessage,
            mergeThreshold,
            minTicksDisplayed,
            timelineEnd
        }),
        []
    );
    return {
        logs: mergedLogs,
        timelineStyle
    };
};

const mapStateToProps = (state, props) => {
    const hubTimezone = getTimezone(state);
    const { logs, timelineStyle } = getLogsData({ ...props, hubTimezone });

    return {
        logs,
        timelineStyle
    };
};

export default connect(mapStateToProps)(Logs);



// WEBPACK FOOTER //
// ./src/components/widgets/logs-timeline/logs/index.js